import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, ArrowLeft, PencilSimple, Check, X } from '@phosphor-icons/react';
import api from '../utils/api';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const EMPTY_DIMS = {
  height:   { inches: '', cm: '' },
  diameter: { inches: '', cm: '' },
  width:    { inches: '', cm: '' },
  length:   { inches: '', cm: '' },
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedFinish, setSelectedFinish] = useState('');
  const [selectedImage, setSelectedImage] = useState(0);
  const [editingDims, setEditingDims] = useState(false);
  const [dimsDraft, setDimsDraft] = useState(EMPTY_DIMS);
  const [savingDims, setSavingDims] = useState(false);
  const { addToCart } = useCart();

  const finishOptions = ['Glossy', 'Matte'];

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await api.get(`/products/${id}`);
      setProduct(response.data);
      if (response.data.colors && response.data.colors.length > 0) {
        setSelectedColor(response.data.colors[0]);
      }
      setSelectedFinish('Matte');
      setDimsDraft(response.data.dimensions || EMPTY_DIMS);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Product not found');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    try {
      await addToCart(product, quantity, { color: selectedColor, finish: selectedFinish });
      toast.success('Added to cart!');
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  const handleSaveDims = async () => {
    setSavingDims(true);
    try {
      const res = await api.patch(`/admin/products/${product.id}/dimensions`, dimsDraft);
      setProduct(p => ({ ...p, dimensions: res.data.dimensions }));
      setEditingDims(false);
      toast.success('Dimensions saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save dimensions');
    } finally {
      setSavingDims(false);
    }
  };

  const handleCancelDims = () => {
    setDimsDraft(product.dimensions || EMPTY_DIMS);
    setEditingDims(false);
  };

  // Compute the images to show based on the selected colour.
  // If images_by_color[selectedColor] exists AND is non-empty → use it.
  // Otherwise fall back to product.images (default gallery) — never show a broken image to customers.
  const activeImages = useMemo(() => {
    if (!product) return [];
    const byColor = product.images_by_color || {};
    const forColor = selectedColor && Array.isArray(byColor[selectedColor]) ? byColor[selectedColor] : [];
    if (forColor.length > 0) return forColor;
    return (product.images && product.images.length > 0) ? product.images : (product.image_url ? [product.image_url] : []);
  }, [product, selectedColor]);

  // Reset thumbnail selection when the colour (and therefore the gallery) changes
  useEffect(() => {
    setSelectedImage(0);
  }, [selectedColor]);

  // Admin helper: does this colour have its own dedicated images?
  const colorHasImages = (color) => {
    const list = product?.images_by_color?.[color];
    return Array.isArray(list) && list.length > 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="loading-spinner" data-testid="loading-spinner" />
      </div>
    );
  }

  if (!product) return null;

  return (
    <div data-testid="product-detail-page" className="min-h-screen pt-24 pb-16 bg-[#F4F4F4]">
      <div className="max-w-7xl mx-auto px-6">
        <button
          data-testid="back-to-products-button"
          onClick={() => navigate('/products')}
          className="flex items-center gap-2 text-sm text-[#7a5e75] hover:text-[#641b4d] mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          Back to Products
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <div className="bg-[#F4F4F4] border border-[#E5E2DC] overflow-hidden flex items-center justify-center p-4">
              <img
                src={activeImages[selectedImage] || activeImages[0] || product.image_url}
                alt={`${product.name}${selectedColor ? ' — ' + selectedColor : ''}`}
                data-testid="product-detail-image"
                className="w-full max-h-[600px] object-contain"
              />
            </div>
            {activeImages.length > 1 && (
              <div data-testid="product-image-gallery" className="flex gap-3">
                {activeImages.map((img, index) => (
                  <button
                    key={`${selectedColor}-${index}`}
                    data-testid={`product-thumbnail-${index}`}
                    onClick={() => setSelectedImage(index)}
                    className={`w-20 h-20 border-2 overflow-hidden transition-all ${
                      selectedImage === index ? 'border-[#641b4d]' : 'border-[#E5E2DC] hover:border-[#7a5e75]'
                    }`}
                  >
                    <img src={img} alt={`${product.name} view ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex flex-col"
          >
            {product.tag && (
              <span data-testid="product-detail-tag" className="text-xs uppercase tracking-widest text-[#bc64aa] mb-3">
                {product.tag}
              </span>
            )}
            <h1 data-testid="product-detail-name" className="text-4xl font-light tracking-tight mb-4">
              {product.name}
            </h1>
            <p data-testid="product-detail-price" className="text-3xl font-semibold text-[#641b4d] mb-1">
              ₹{product.price.toFixed(2)}
            </p>
            {product.price_note && (
              <p data-testid="product-price-note" className="text-xs text-[#7a5e75] italic mb-6">
                {product.price_note}
              </p>
            )}
            {!product.price_note && <div className="mb-6" />}
            <p data-testid="product-detail-description" className="text-base text-[#7a5e75] leading-relaxed mb-8 whitespace-pre-line">
              {product.description}
            </p>

            <div className="space-y-6 mb-8">
              {/* Dimensions Table (replaces Size section; Shape/Description section removed) */}
              <div data-testid="dimensions-block">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs uppercase tracking-widest text-[#7a5e75] block">Dimensions</span>
                  {user?.is_admin && !editingDims && (
                    <button
                      data-testid="edit-dimensions-btn"
                      onClick={() => setEditingDims(true)}
                      className="text-[10px] uppercase tracking-wider text-[#bc64aa] hover:text-[#641b4d] flex items-center gap-1"
                    >
                      <PencilSimple size={12} weight="bold" /> Edit
                    </button>
                  )}
                  {user?.is_admin && editingDims && (
                    <div className="flex items-center gap-2">
                      <button data-testid="save-dimensions-btn" disabled={savingDims} onClick={handleSaveDims} className="text-[10px] uppercase tracking-wider text-white bg-[#641b4d] hover:bg-[#8c1666] px-2 py-1 flex items-center gap-1 disabled:opacity-50">
                        <Check size={12} weight="bold" /> {savingDims ? 'Saving…' : 'Save'}
                      </button>
                      <button data-testid="cancel-dimensions-btn" onClick={handleCancelDims} className="text-[10px] uppercase tracking-wider text-[#7a5e75] hover:text-[#641b4d] flex items-center gap-1">
                        <X size={12} weight="bold" /> Cancel
                      </button>
                    </div>
                  )}
                </div>
                <table data-testid="dimensions-table" className="w-full border border-[#E5E2DC] text-sm">
                  <thead>
                    <tr className="bg-[#F4F4F4]">
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#7a5e75] font-medium border-b border-[#E5E2DC]">Measurement</th>
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#7a5e75] font-medium border-b border-[#E5E2DC]">Inches</th>
                      <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider text-[#7a5e75] font-medium border-b border-[#E5E2DC]">cm</th>
                    </tr>
                  </thead>
                  <tbody>
                    {['height', 'diameter', 'width', 'length'].map((k) => {
                      const label = k.charAt(0).toUpperCase() + k.slice(1);
                      const cell = (dimsDraft && dimsDraft[k]) || { inches: '', cm: '' };
                      const readCell = (product.dimensions && product.dimensions[k]) || { inches: '', cm: '' };
                      return (
                        <tr key={k} className="border-t border-[#E5E2DC]">
                          <td className="px-3 py-2 text-[#641b4d] font-medium">{label}</td>
                          <td className="px-3 py-2">
                            {editingDims ? (
                              <input
                                type="text"
                                data-testid={`dim-${k}-inches`}
                                value={cell.inches}
                                onChange={(e) => setDimsDraft(d => ({ ...d, [k]: { ...d[k], inches: e.target.value } }))}
                                className="w-full border border-[#E5E2DC] px-2 py-1 text-sm"
                                placeholder="—"
                              />
                            ) : (
                              <span data-testid={`dim-${k}-inches-value`} className="text-[#3a2a35]">{readCell.inches || '—'}</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {editingDims ? (
                              <input
                                type="text"
                                data-testid={`dim-${k}-cm`}
                                value={cell.cm}
                                onChange={(e) => setDimsDraft(d => ({ ...d, [k]: { ...d[k], cm: e.target.value } }))}
                                className="w-full border border-[#E5E2DC] px-2 py-1 text-sm"
                                placeholder="—"
                              />
                            ) : (
                              <span data-testid={`dim-${k}-cm-value`} className="text-[#3a2a35]">{readCell.cm || '—'}</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Colour Options */}
              {product.colors && product.colors.length > 0 && (
                <div>
                  <span className="text-xs uppercase tracking-widest text-[#7a5e75] mb-3 block">Colour</span>
                  <div className="flex flex-wrap gap-2">
                    {product.colors.map(color => {
                      const isSelected = selectedColor === color;
                      const missingImages = user?.is_admin && !colorHasImages(color);
                      return (
                        <button
                          key={color}
                          data-testid={`color-option-${color.toLowerCase().replace(/\s+/g, '-')}`}
                          onClick={() => setSelectedColor(color)}
                          className={`relative px-5 py-2 text-sm tracking-wider transition-all ${
                            isSelected
                              ? 'bg-[#641b4d] text-white ring-2 ring-offset-2 ring-[#641b4d]'
                              : 'bg-white text-[#641b4d] border border-[#E5E2DC] hover:border-[#641b4d]'
                          }`}
                          title={missingImages ? `No dedicated images uploaded for ${color}. Customers will see the default gallery.` : color}
                        >
                          {color}
                          {missingImages && (
                            <span
                              data-testid={`no-images-badge-${color.toLowerCase().replace(/\s+/g, '-')}`}
                              className="absolute -top-1 -right-1 bg-[#ffd034] text-[#3a2a35] text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center leading-none"
                              aria-label="Missing images for this colour"
                            >
                              !
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {user?.is_admin && product.colors.some(c => !colorHasImages(c)) && (
                    <p data-testid="admin-missing-images-notice" className="text-[11px] text-[#7a5e75] mt-2 italic">
                      Admin note: Colours marked with <span className="inline-block w-3 h-3 rounded-full bg-[#ffd034] text-[8px] font-bold text-[#3a2a35] leading-3 text-center align-middle">!</span> have no dedicated images and will show the default gallery to customers. Upload images to complete the colour variants.
                    </p>
                  )}
                  <p className="text-xs text-[#7a5e75] mt-3">
                    For customisation, please visit the{' '}
                    <Link to="/custom-inquiry" data-testid="custom-order-link" className="text-[#bc64aa] hover:underline">
                      custom order section
                    </Link>.
                  </p>
                </div>
              )}

              {/* Finish Options */}
              <div>
                <span className="text-xs uppercase tracking-widest text-[#7a5e75] mb-3 block">Finish</span>
                <div className="flex flex-wrap gap-2">
                  {finishOptions.map(finish => (
                    <button
                      key={finish}
                      data-testid={`finish-option-${finish.toLowerCase()}`}
                      onClick={() => setSelectedFinish(finish)}
                      className={`px-5 py-2 text-sm tracking-wider transition-all ${
                        selectedFinish === finish
                          ? 'bg-[#641b4d] text-white'
                          : 'bg-white text-[#641b4d] border border-[#E5E2DC] hover:border-[#641b4d]'
                      }`}
                    >
                      {finish}
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity */}
              <div>
                <span className="text-xs uppercase tracking-widest text-[#7a5e75] mb-3 block">Quantity</span>
                <div className="flex items-center gap-4">
                  <button
                    data-testid="decrease-quantity-button"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-10 h-10 border border-[#E5E2DC] flex items-center justify-center hover:bg-[#F4F4F4] transition-colors"
                  >
                    -
                  </button>
                  <span data-testid="quantity-display" className="text-lg font-semibold w-12 text-center">{quantity}</span>
                  <button
                    data-testid="increase-quantity-button"
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-10 h-10 border border-[#E5E2DC] flex items-center justify-center hover:bg-[#F4F4F4] transition-colors"
                  >
                    +
                  </button>
                </div>
              </div>

              <div>
                <span className="text-xs uppercase tracking-widest text-[#7a5e75] mb-2 block">Stock</span>
                <p data-testid="product-detail-stock" className={`text-base ${
                  product.stock > 10 ? 'text-green-600' : product.stock > 0 ? 'text-orange-600' : 'text-red-600'
                }`}>
                  {product.stock > 0 ? `${product.stock} in stock` : 'Out of stock'}
                </p>
              </div>
            </div>

            <button
              data-testid="add-to-cart-button"
              onClick={handleAddToCart}
              disabled={product.stock === 0}
              className="btn-primary flex items-center justify-center gap-2 w-full disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ShoppingCart size={20} weight="bold" />
              Add to Cart
            </button>

            <button
              data-testid="bulk-inquiry-button"
              onClick={() => navigate(`/bulk-inquiry?product=${product.id}`)}
              className="btn-secondary w-full mt-4"
            >
              Bulk Order Inquiry
            </button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
